import React, { useMemo, useState } from 'react';
import { useLocalStorage } from '../useLocalStorage';
import styles from './ConnectionsStep.module.css';
import type { ConnectionsGroup } from '../../types';

export interface ConnectionsStepProps {
  groups: ConnectionsGroup[]; // 4 groups of 4 items each
  // injected
  storageKey: string;
  initiallySolved?: boolean;
  disabled?: boolean;
  onSolved?: () => void;
}

type Persisted = { remaining: string[]; solvedGroups: ConnectionsGroup[]; status: 'playing' | 'won' };

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export const ConnectionsStep: React.FC<ConnectionsStepProps> = ({ groups, storageKey, initiallySolved = false, disabled = false, onSolved }) => {
  const fullItemList = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const defaultState: Persisted = useMemo(() => ({ remaining: shuffle(fullItemList), solvedGroups: [], status: initiallySolved ? 'won' : 'playing' }), [fullItemList, initiallySolved]);
  const [state, setState] = useLocalStorage<Persisted>(`${storageKey}`, defaultState);

  const [selected, setSelected] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<string | null>(null);

  const isWon = state.status === 'won';

  function toggle(item: string) {
    if (disabled || isWon) return;
    setSelected((prev) => (prev.includes(item) ? prev.filter((x) => x !== item) : prev.length < 4 ? [...prev, item] : prev));
    setFeedback(null);
  }

  function submit() {
    if (disabled || isWon) return;
    if (selected.length !== 4) {
      setFeedback('Please select exactly 4 items to form a group.');
      return;
    }
    const match = groups.find((g) => g.items.every((it) => selected.includes(it)));
    if (!match) {
      setFeedback('Almost there! Try a different combination.');
      return;
    }
    const remaining = state.remaining.filter((it) => !selected.includes(it));
    const solvedGroups = [...state.solvedGroups, match];
    const next: Persisted = { remaining, solvedGroups, status: solvedGroups.length === 4 ? 'won' : 'playing' };
    setState(next);
    setSelected([]);
    setFeedback('Correct!');
    if (next.status === 'won' && onSolved) onSolved();
  }

  function shuffleBoard() {
    if (disabled || isWon) return;
    setState((prev) => ({ ...prev, remaining: shuffle(prev.remaining) }));
  }

  return (
    <div className={styles.container}>
      {state.solvedGroups.length > 0 && (
        <div className={styles.lockedGroups}>
          {state.solvedGroups.map((g, idx) => (
            <div key={`solved-${idx}`} className={`${styles.lockedGroup} ${styles[g.difficulty] || ''}`}>
              <div className={styles.lockedTitle}>{g.title}</div>
              <div className={styles.lockedItems}>
                {g.items.map((it) => (
                  <span key={it} className={styles.pillSmall}>{it}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={styles.grid} aria-label="connections grid">
        {state.remaining.map((it) => (
          <button
            key={it}
            type="button"
            className={`${styles.tile} ${selected.includes(it) ? styles.selected : ''} ${disabled || isWon ? styles.disabled : ''}`}
            onClick={() => toggle(it)}
            disabled={disabled || isWon}
          >
            {it}
          </button>
        ))}
      </div>

      <div className={styles.actions}>
        <button className={styles.btn} onClick={shuffleBoard} disabled={disabled || isWon}>Shuffle</button>
        <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={submit} disabled={disabled || isWon || selected.length !== 4}>Submit group</button>
      </div>

      {feedback && (
        <div className={`${styles.feedback} ${styles[getFeedbackType(feedback)]}`}>
          <div className={styles.feedbackIcon}>
            {getFeedbackIcon(feedback)}
          </div>
          <div className={styles.feedbackText}>
            {feedback}
          </div>
        </div>
      )}
    </div>
  );
}

function getFeedbackType(feedback: string): string {
  if (feedback === 'Correct!') return 'success';
  if (feedback === 'Almost there! Try a different combination.') return 'error';
  if (feedback === 'Please select exactly 4 items to form a group.') return 'info';
  return 'info';
}

function getFeedbackIcon(feedback: string): string {
  if (feedback === 'Correct!') return '🎉';
  if (feedback === 'Almost there! Try a different combination.') return '💡';
  if (feedback === 'Please select exactly 4 items to form a group.') return 'ℹ️';
  return 'ℹ️';
}

export default ConnectionsStep;


